import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type TrendDirection = 'up' | 'down' | 'neutral';

export interface AbsoluteChange {
  delta: number | null;
  direction: TrendDirection;
}

/**
 * Compute the rounded absolute change between two values.
 *
 * `precision` is the number of decimal places kept when deciding direction —
 * a delta that rounds to zero at the chosen precision reads as `neutral`,
 * not `up`/`down`. This avoids "+0.0s" ever rendering with a red trend icon.
 */
export const computeAbsoluteChange = (
  current: number | null | undefined,
  previous: number | null | undefined,
  precision = 0
): AbsoluteChange => {
  if (current == null || previous == null) {
    return { delta: null, direction: 'neutral' };
  }
  const factor = 10 ** precision;
  const rounded = Math.round((current - previous) * factor) / factor;
  if (rounded === 0) {
    return { delta: 0, direction: 'neutral' };
  }
  return { delta: rounded, direction: rounded > 0 ? 'up' : 'down' };
};

const COLORS: Record<Exclude<TrendDirection, 'neutral'>, string> = {
  up: 'text-success',
  down: 'text-destructive',
};

const INVERTED_COLORS: Record<Exclude<TrendDirection, 'neutral'>, string> = {
  up: 'text-destructive',
  down: 'text-success',
};

type TrendIconComponent = typeof TrendingUpIcon | typeof TrendingDownIcon;

const DEFAULT_ICONS: Record<Exclude<TrendDirection, 'neutral'>, TrendIconComponent> = {
  up: TrendingUpIcon,
  down: TrendingDownIcon,
};

const INVERTED_ICONS: Record<Exclude<TrendDirection, 'neutral'>, TrendIconComponent> = {
  up: TrendingDownIcon,
  down: TrendingUpIcon,
};

const getTrendIcon = (
  direction: TrendDirection,
  invertColors: boolean | undefined
): TrendIconComponent | null => {
  if (direction === 'neutral') {
    return null;
  }
  return invertColors ? INVERTED_ICONS[direction] : DEFAULT_ICONS[direction];
};

type Size = 'sm' | 'md';

const sizeClasses: Record<Size, { text: string; icon: string; gap: string }> = {
  sm: { text: 'text-xs', icon: 'size-3', gap: 'gap-1' },
  md: { text: 'text-sm', icon: 'size-3.5', gap: 'gap-1' },
};

interface Props {
  current: number | null | undefined;
  previous: number | null | undefined;
  /**
   * Formats the rounded delta into a unit-aware string.
   * Receives a *signed* delta — implementations should add a leading "+"
   * for positive values; negatives already carry their sign.
   */
  formatDelta: (delta: number) => string;
  /** Decimal places used for rounding the delta. Defaults to 0 (whole numbers). */
  precision?: number;
  comparisonLabel?: string;
  invertColors?: boolean;
  size?: Size;
  isLoading?: boolean;
  className?: string;
}

export const AbsoluteChangeIndicator = ({
  current,
  previous,
  formatDelta,
  precision = 0,
  comparisonLabel,
  invertColors,
  size = 'sm',
  isLoading,
  className,
}: Props) => {
  if (isLoading) {
    return <Skeleton className={cn('h-4 w-24', className)} />;
  }

  const { delta, direction } = computeAbsoluteChange(current, previous, precision);

  const text = delta == null ? 'N/A' : formatDelta(delta);
  const neutralColor = 'text-muted-foreground';
  const baseColor =
    direction === 'neutral'
      ? neutralColor
      : invertColors
        ? INVERTED_COLORS[direction]
        : COLORS[direction];

  const TrendIcon = getTrendIcon(direction, invertColors);
  const { text: textSize, icon: iconSize, gap } = sizeClasses[size];

  return (
    <div className={cn('flex items-center font-medium', gap, textSize, className)}>
      <span className={baseColor}>{text}</span>
      {comparisonLabel && <span className="text-muted-foreground">{comparisonLabel}</span>}
      {TrendIcon && <TrendIcon className={cn(iconSize, baseColor)} />}
    </div>
  );
};
